import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FinanceType } from "@cf247/pricing/dist/enums";
import type { MonthlyPrice } from "@cf247/pricing/dist/types";
import { formatUnderwritingQuoteAmount } from "@/helpers/underwritingDashboard";

const GBP = formatUnderwritingQuoteAmount;

const PCT = (n: number | null | undefined) => (n != null ? `${n.toFixed(1)}%` : "—");

interface QuoteDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: MonthlyPrice | null;
  onPropose: (quote: MonthlyPrice) => void;
  /** Whether this quote is currently being proposed. */
  proposing: boolean;
  canPropose: boolean;
}

/**
 * Full breakdown for a single quote — the figures that don't fit in the
 * simplified Quotes table are surfaced here via the per-row details button.
 */
export function QuoteDetailsDialog({
  open,
  onOpenChange,
  quote,
  onPropose,
  proposing,
  canPropose,
}: QuoteDetailsDialogProps) {
  if (!quote) return null;

  const product = quote.financeType === FinanceType.PCP ? "PCP" : "HP";

  // CAP LTV = loan ÷ CAP retail valuation. MonthlyPrice has no LTV field, so
  // this is derived from the CAP data attached to the quote.
  const capValue = quote.cap?.retail ?? null;
  const capLtv =
    capValue && capValue > 0 && quote.balanceToFinance != null
      ? `${Math.round((quote.balanceToFinance / capValue) * 100)}%`
      : "—";

  const rows: { label: string; value: string }[] = [
    { label: "Lender", value: quote.lenderName ?? "—" },
    { label: "Product", value: product },
    { label: "Term", value: quote.term != null ? `${quote.term} mo` : "—" },
    { label: "Monthly", value: GBP(quote.monthlyPayment) },
    { label: "APR", value: PCT(quote.apr) },
    { label: "Cash Price", value: GBP(quote.vehiclePrice) },
    { label: "Deposit", value: GBP(quote.deposit) },
    { label: "Part Ex Value", value: GBP(quote.pxValue) },
    { label: "Part Ex Settlement", value: GBP(quote.pxSettlement) },
    { label: "Loan Amount", value: GBP(quote.balanceToFinance) },
    { label: "Final Monthly", value: GBP(quote.finalMonthlyPayment) },
    { label: "Total Repayable", value: GBP(quote.totalAmountPayable) },
    { label: "Total Charges", value: GBP(quote.costOfCredit) },
    { label: "Commission", value: GBP(quote.commissionAmount) },
    { label: "CAP LTV", value: capLtv },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Quote details</DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-1 pr-3">
          {rows.map(({ label, value }) => (
            <div
              key={label}
              className="flex items-center justify-between gap-4 border-b border-border py-2.5 last:border-0"
            >
              <span className="text-[12px] text-muted-foreground">{label}</span>
              <span className="text-[12px] font-semibold text-foreground tabular-nums">{value}</span>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
          <Button
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => onPropose(quote)}
            disabled={proposing || !canPropose}
            title={!canPropose ? "Claim this request to propose to a lender" : undefined}
          >
            {proposing && <Loader2 className="size-3.5 animate-spin mr-1" />}
            Propose
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
